import { useEffect, useState } from 'react'
import { Button, Card } from '@heroui/react'
import { api } from '../lib/api'
import { mayWriteSetup } from '../lib/branchConfig'
import { explain } from '../lib/staffErrors'
import { passUnsaved, useUnsaved } from '../lib/unsaved'
import type { Branch, BranchDoc, ProfileUnit, Snapshot } from '../lib/types'
import { Icon } from '../ui/icons'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { BranchDetailsPage, OpenState, openState } from './Branches'
import { BranchVersion } from './BranchVersion'
import { SETUP_CHANGED } from './setupChanged'
import { BranchEquipment, useMachineData } from './equipment/BranchEquipment'
import { BranchSetup, DayPage } from './equipment/BranchSetup'
import { MachinePage } from './equipment/MachinePage'
import { useBranchDraft } from './equipment/useBranchDraft'
import { Confirm, useFlash } from './sectionKit'
import { AskBar } from './setupKit'
import { DangerCard, EditLayout, IconTile, PageHead, SaveActions, SummaryCard, useSubPage } from './settingsKit'
import '../styles/branchpage.css'
import '../styles/branchmachines.css'

/**
 * One branch's own page, at its own address (#branches/CODE): its profiles and
 * business day, its machines and the design each shows, and the live version. The
 * setup is held here and saved whole; a save is live. A machine (…/machine/<code>),
 * the business day (…/hours) and the branch's details (…/details) open as inner
 * pages, each saving on its own Save and returning here.
 */

const MACHINE = 'machine'
const HOURS = 'hours'
const DETAILS = 'details'

export type BranchView =
  | { at: 'branch' | 'hours' | 'details' }
  | { at: 'machine'; gi: number; j: number }
  /** The setup is still on its way. */
  | { at: 'wait' }
  /** The address names no page of this branch (a stale machine code): back to the branch. */
  | { at: 'stray' }

/** The page the steps after #branches/<code> name, given the branch's setup as read so far. */
export function branchView(
  path: readonly string[],
  d: { draft: BranchDoc | null; equipment: { document: { units: ProfileUnit[] } } | null; error: string },
): BranchView {
  const [step = '', code = ''] = path
  if (step === '') return { at: 'branch' }
  if (step === DETAILS) return { at: 'details' }
  if (!d.draft) return d.error ? { at: 'stray' } : { at: 'wait' }
  if (step === HOURS && path.length === 1) return { at: 'hours' }
  if (step === MACHINE && path.length === 2) {
    const units = d.draft.units
    const gi = units.findIndex((g) => g.instances.some((i) => i.code === code))
    const known = gi >= 0 && d.equipment?.document.units.some((u) => u.id === units[gi]!.profileUnitId)
    if (known) return { at: 'machine', gi, j: units[gi]!.instances.findIndex((i) => i.code === code) }
  }
  return { at: 'stray' }
}

export interface SectionLink {
  key: string
  label: string
}

/** Desks, queues and who waits now, from the branch's live snapshot. */
function useFloorCounts(branchId: string) {
  const [counts, setCounts] = useState<{ desks: number; queues: number; waiting: number } | null>(null)
  useEffect(() => {
    let alive = true
    const read = () =>
      api
        .snapshot(branchId)
        .then((s: Snapshot) => {
          if (!alive) return
          setCounts({ desks: s.counters.length, queues: s.queues.length, waiting: s.queues.reduce((n, q) => n + q.waiting, 0) })
        })
        .catch(() => alive && setCounts(null))
    void read()
    window.addEventListener(SETUP_CHANGED, read)
    return () => {
      alive = false
      window.removeEventListener(SETUP_CHANGED, read)
    }
  }, [branchId])
  return counts
}

export function BranchPage({
  branch,
  links,
  onBack,
  onSaved,
}: {
  branch: Branch
  links: SectionLink[]
  onBack: () => void
  onSaved: () => Promise<void>
}) {
  const { lang, say } = useStaffText()
  const session = useSession()
  const { flash, toast } = useFlash()
  const d = useBranchDraft(branch.id, session?.identity.owner ? branch.tenantId : undefined)
  const sub = useSubPage()
  const [leaving, setLeaving] = useState(false)
  const canWrite = !session || mayWriteSetup(session.identity)
  const mayReset = Boolean(session?.identity.permissions.includes('branch.manage'))
  const [resetBusy, setResetBusy] = useState(false)
  const counts = useFloorCounts(branch.id)
  const machines = useMachineData(branch)
  const unsaved = d.dirty || !!d.pending
  // Under this branch's address, so its own inner pages keep unsaved edits without asking.
  useUnsaved(unsaved, sub.here || undefined)

  const view = branchView(sub.path, d)
  const stray = view.at === 'stray'
  useEffect(() => {
    if (stray) sub.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stray])

  const st = openState(branch, lang, say)
  const go = (key: string) => {
    window.location.hash = `${key}/${encodeURIComponent(branch.code)}`
  }
  const shortcuts = links.filter((l) => l.key === 'alerts' || l.key === 'approvals')
  const back = () => (unsaved ? setLeaving(true) : onBack())
  const toBranch = () => sub.close()
  const crumbs = [
    {
      label: say('admin.section.branchManagement'),
      // With unsaved edits, the branch page asks before the list.
      onOpen: () => {
        if (!unsaved) return onBack()
        toBranch()
        setLeaving(true)
      },
    },
    { label: branch.name, onOpen: toBranch },
  ]

  const reset = async () => {
    setResetBusy(true)
    try {
      const r = await api.resetDay(branch.id)
      flash(say('bp.resetDone', { n: r.closed }))
    } catch (err) {
      flash(explain(err, lang, 'common.unreachable'))
    } finally {
      setResetBusy(false)
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [view.at])

  // A deep link waits for the setup before it opens the page it names.
  if (view.at === 'wait' || stray) return <div className="bp" />
  if (view.at === 'machine') {
    return (
      <div className="bp">
        <MachinePage
          branch={branch}
          d={d}
          data={machines}
          gi={view.gi}
          j={view.j}
          canWrite={canWrite}
          crumbs={crumbs}
          onClose={toBranch}
          flash={flash}
        />
        {toast}
      </div>
    )
  }
  if (view.at === 'hours') {
    return (
      <div className="bp">
        <DayPage d={d} crumbs={crumbs} onClose={toBranch} flash={flash} />
        {toast}
      </div>
    )
  }
  if (view.at === 'details') {
    return (
      <div className="bp">
        <BranchDetailsPage branch={branch} crumbs={crumbs} onClose={toBranch} onSaved={onSaved} flash={flash} />
        {toast}
      </div>
    )
  }

  return (
    <div className="bp">
      <PageHead
        crumbs={[{ label: say('admin.section.branchManagement'), onOpen: back }]}
        title={branch.name}
        line={`${branch.code} · ${branch.timezone}`}
        actions={
          <>
            <BranchVersion
              branchId={branch.id}
              onDocumentReplaced={() => {
                void d.reload()
                void onSaved()
              }}
            />
            <Button variant="secondary" onPress={() => sub.open(DETAILS)}>
              <Icon name="edit" size={16} /> {say('bp.edit')}
            </Button>
            {canWrite && (
              <SaveActions
                dirty={d.dirty}
                busy={d.busy || !!d.pending}
                onDiscard={() => void d.reload()}
                onSave={() => void d.save().then((ok) => ok && flash(say('common.saved')))}
              />
            )}
          </>
        }
      />

      {leaving && (
        <AskBar
          className="bp-leave"
          title={say('eq.branch.leaveAsk')}
          actions={
            <>
              <Button variant="ghost" size="sm" autoFocus onPress={() => setLeaving(false)}>
                {say('ui.keepEditing')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onPress={() => {
                  passUnsaved()
                  onBack()
                }}
              >
                {say('ui.discard')}
              </Button>
            </>
          }
        />
      )}

      <EditLayout
        side={
          <>
            <SummaryCard
              band={branch.active ? say('settings.branch.band') : say('settings.branches.off')}
              tile={<IconTile icon="organisations" size="xl" />}
              name={branch.name}
              sub={branch.code}
              state={<OpenState st={st} />}
              stats={[
                { label: say('settings.branch.desks'), value: counts?.desks ?? '–' },
                { label: say('settings.branch.queues'), value: counts?.queues ?? '–' },
                { label: say('settings.branch.waiting'), value: counts?.waiting ?? '–' },
              ]}
            />
            {shortcuts.length > 0 && (
              <nav className="bp-links" aria-label={say('admin.group.branch')}>
                {shortcuts.map((l) => (
                  <Button key={l.key} variant="secondary" size="sm" onPress={() => go(l.key)}>
                    {l.label} <Icon name="right" size={14} />
                  </Button>
                ))}
              </nav>
            )}
            {mayReset && branch.active && (
              <DangerCard
                title={say('settings.branch.resetTitle')}
                line={say('settings.branch.resetLine')}
                action={
                  <Confirm
                    ask={say('bp.reset')}
                    question={say('bp.resetTitle', { name: branch.name })}
                    yes={say('bp.resetYes')}
                    keep={say('common.keep')}
                    busy={resetBusy}
                    onYes={() => void reset()}
                  />
                }
              />
            )}
          </>
        }
      >
        <BranchSetup branch={branch} d={d} canWrite={canWrite} onHours={() => sub.open(HOURS)} />
      </EditLayout>
      <Card className="bpm-machines">
        <BranchEquipment
          branch={branch}
          d={d}
          canWrite={canWrite}
          data={machines}
          onOpen={(g, k) => {
            const code = d.draft?.units[g]?.instances[k]?.code
            if (code) sub.open(MACHINE, code)
          }}
        />
      </Card>

      {toast}
    </div>
  )
}
